import type {
  CodexCredentialDelivery,
  CodexSubscriptionRefreshInput,
  CodexSubscriptionRefreshResult,
} from "@skaile/workspaces/types";
import * as z from "zod";
const identifier = z.string().min(1).max(4096);
const timestamp = z.iso.datetime({ offset: true });
const common = {
  configId: identifier,
  credentialVersion: z.number().int().nonnegative().safe(),
  mintedAt: timestamp,
};
const subscription = z.strictObject({
  ...common,
  kind: z.literal("codex-subscription"),
  accessToken: z.string().min(1).max(65536),
  chatgptAccountId: identifier,
  chatgptPlanType: identifier.optional(),
  expiresAt: timestamp,
});
const delivery = z.discriminatedUnion("kind", [
  subscription,
  z.strictObject({
    ...common,
    kind: z.literal("openai-api-key"),
    apiKey: z.string().min(1).max(65536),
  }),
]);

/** Validate credential shape and provenance without including input values in errors. */
export function parseCodexCredential(value: unknown, now = Date.now()): CodexCredentialDelivery {
  const parsed = delivery.safeParse(value);
  if (!parsed.success) throw new Error("Invalid managed AI credential");
  const credential = parsed.data;
  const mintedAt = Date.parse(credential.mintedAt);
  // Issuer and runtime clocks may differ slightly; expiry remains strictly unexpired.
  if (
    mintedAt > now + 60_000 ||
    (credential.kind === "codex-subscription" &&
      (Date.parse(credential.expiresAt) <= now || Date.parse(credential.expiresAt) <= mintedAt))
  )
    throw new Error("Invalid managed AI credential timestamps");
  return credential;
}

/** Validate runtime-only refresh input against the authenticated session's pinned delivery. */
export function parseCodexRefreshInput(
  value: unknown,
  pinned: Extract<CodexCredentialDelivery, { kind: "codex-subscription" }>,
): CodexSubscriptionRefreshInput {
  const parsed = z
    .strictObject({
      kind: z.literal("codex-subscription"),
      configId: identifier,
      credentialVersion: common.credentialVersion,
      reason: z.enum(["initial", "refresh", "retry-401"]),
    })
    .safeParse(value);
  if (
    !parsed.success ||
    parsed.data.configId !== pinned.configId ||
    parsed.data.credentialVersion !== pinned.credentialVersion
  )
    throw new Error("Invalid managed AI refresh binding");
  return parsed.data;
}

/** Accept refreshed subscription credentials only within the existing config/account binding. */
export function parseCodexRefreshResult(
  value: unknown,
  pinned: Extract<CodexCredentialDelivery, { kind: "codex-subscription" }>,
  now = Date.now(),
): CodexSubscriptionRefreshResult {
  const parsed = z
    .discriminatedUnion("ok", [
      z.strictObject({ ok: z.literal(true), credential: subscription }),
      z.strictObject({
        ok: z.literal(false),
        code: z.enum(["not-configured", "revoked", "provider-error", "backend-error"]),
        message: z.string(),
      }),
    ])
    .safeParse(value);
  if (!parsed.success) throw new Error("Invalid managed AI refresh result");
  if (!parsed.data.ok) return { ...parsed.data, message: "Managed subscription refresh failed" };
  const credential = parseCodexCredential(parsed.data.credential, now);
  if (
    credential.kind !== "codex-subscription" ||
    credential.configId !== pinned.configId ||
    credential.chatgptAccountId !== pinned.chatgptAccountId ||
    credential.credentialVersion < pinned.credentialVersion
  )
    throw new Error("Invalid managed AI refresh binding");
  return { ok: true, credential };
}
